import React, { useState, useEffect } from "react";
import { FiUser, FiBriefcase } from "react-icons/fi";
import { OutlinedInput } from "../../../../../../components/OutlinedInput";
import { FloatingSelect } from "../../../../../../components/FloatingSelect";
import { PrimaryButton } from "../../../../../../components/PrimaryButton";
import { SecondaryButton } from "../../../../../../components/SecondaryButton";
import { showToast } from "../../../../../../components/CustomToast";
import { IPersonFormData, ILicenseeFormData } from "../../dtos.d";
import {
  SectionBlock,
  SectionHeader,
  SectionTitleWrapper,
  SectionTitleRow,
  SectionTitle,
  SectionSubtitle,
  PersonTypeSwitchWrapper,
  SwitchContainer,
  SwitchOption,
  InputGroup,
  Row,
  FlexInput,
  Footer,
} from "./styles";

const cpfMask = (value: string) => {
  return value
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})/, "$1-$2")
    .replace(/(-\d{2})\d+?$/, "$1")
    .substring(0, 14);
};

const cnpjMask = (value: string) => {
  return value
    .replace(/\D/g, "")
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2")
    .substring(0, 18);
};

const phoneMask = (value: string) => {
  const clean = value.replace(/\D/g, "");
  if (clean.length <= 10) {
    return clean
      .replace(/^(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .substring(0, 14);
  }
  return clean
    .replace(/^(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .substring(0, 15);
};

interface StepRegistrationDataProps {
  personFormData: IPersonFormData;
  setPersonFormData: React.Dispatch<React.SetStateAction<IPersonFormData>>;
  licenseeFormData: ILicenseeFormData;
  setLicenseeFormData: React.Dispatch<React.SetStateAction<ILicenseeFormData>>;
  nextStep: () => void;
  prevStep: () => void;
}

export function StepRegistrationData({
  personFormData,
  setPersonFormData,
  licenseeFormData,
  setLicenseeFormData,
  nextStep,
  prevStep,
}: StepRegistrationDataProps) {
  const [profileType, setProfileType] = useState<"PF" | "PJ">("PF");
  const [errors, setErrors] = useState<any>({});

  // Initialize or infer profile type from existing cnpj/companyName
  useEffect(() => {
    if (licenseeFormData.cnpj || licenseeFormData.companyName) {
      setProfileType("PJ");
    } else {
      setProfileType("PF");
    }
  }, [licenseeFormData.cnpj, licenseeFormData.companyName]);

  const handlePersonChange = (field: keyof IPersonFormData, value: any) => {
    setPersonFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev: any) => ({ ...prev, [field]: "" }));
  };

  const handleLicenseeChange = (field: keyof ILicenseeFormData, value: any) => {
    setLicenseeFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev: any) => ({ ...prev, [field]: "" }));
  };

  const handleProfileChange = (type: "PF" | "PJ") => {
    setProfileType(type);
    if (type === "PF") {
      setLicenseeFormData((prev) => ({
        ...prev,
        cnpj: null,
        companyName: null,
        fantasyName: null,
        phone: licenseeFormData.phone || personFormData.cellphone,
      }));
    }
  };

  const validateAndProceed = () => {
    const newErrors: any = {};

    // Validate Representative (Person) fields
    if (!personFormData.name || personFormData.name.trim().length < 3) {
      newErrors.name = "Nome completo é obrigatório";
    }
    if (!personFormData.cpf || personFormData.cpf.length < 14) {
      newErrors.cpf = "CPF inválido";
    }
    if (!personFormData.cellphone || personFormData.cellphone.length < 14) {
      newErrors.cellphone = "Telefone celular inválido";
    }
    if (!personFormData.expertise) {
      newErrors.expertise = "Selecione a área de atuação";
    }
    if (
      personFormData.expertise &&
      personFormData.expertise !== "Técnico em Imobilização" &&
      (!personFormData.professional_number || personFormData.professional_number.trim().length === 0)
    ) {
      newErrors.professional_number = "Número de conselho profissional é obrigatório";
    }

    // Validate Account/Commercial email
    if (!licenseeFormData.email || !/^\S+@\S+\.\S+$/.test(licenseeFormData.email)) {
      newErrors.email = "E-mail inválido";
    }

    if (profileType === "PJ") {
      // Validate Licensee Company fields
      if (!licenseeFormData.cnpj || licenseeFormData.cnpj.length < 18) {
        newErrors.cnpj = "CNPJ inválido";
      }
      if (!licenseeFormData.companyName || licenseeFormData.companyName.trim().length < 3) {
        newErrors.companyName = "Razão Social é obrigatória";
      }
      if (!licenseeFormData.fantasyName || licenseeFormData.fantasyName.trim().length < 2) {
        newErrors.fantasyName = "Nome Fantasia é obrigatório";
      }
      if (!licenseeFormData.phone || licenseeFormData.phone.length < 14) {
        newErrors.phone = "Telefone comercial inválido";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast({
        type: "error",
        message: "Por favor, preencha todos os campos obrigatórios corretamente.",
      });
      return;
    }

    nextStep();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}>
      {/* BLOCO 1: IDENTIFICAÇÃO E DADOS PROFISSIONAIS */}
      <SectionBlock>
        <SectionHeader>
          <SectionTitleWrapper>
            <SectionTitleRow>
              <SectionTitle>
                <FiUser /> Identificação do Profissional Responsável
              </SectionTitle>
            </SectionTitleRow>
            <SectionSubtitle>
              Informe os dados pessoais e profissionais do titular da conta Fix it.
            </SectionSubtitle>
          </SectionTitleWrapper>

          <PersonTypeSwitchWrapper>
            <SwitchContainer>
              <SwitchOption
                type="button"
                selected={profileType === "PF"}
                onClick={() => handleProfileChange("PF")}
              >
                Pessoa Física (PF)
              </SwitchOption>
              <SwitchOption
                type="button"
                selected={profileType === "PJ"}
                onClick={() => handleProfileChange("PJ")}
              >
                Pessoa Jurídica (PJ)
              </SwitchOption>
            </SwitchContainer>
          </PersonTypeSwitchWrapper>
        </SectionHeader>

        <InputGroup>
          <Row>
            <FlexInput flex={2}>
              <OutlinedInput
                inputName="name"
                label="Nome completo"
                handleChange={(val) => handlePersonChange("name", val)}
                value={personFormData.name || ""}
                error={errors.name}
              />
            </FlexInput>
            <FlexInput flex={1}>
              <OutlinedInput
                inputName="cpf"
                label="CPF"
                handleChange={(val) => handlePersonChange("cpf", cpfMask(val))}
                value={personFormData.cpf || ""}
                error={errors.cpf}
              />
            </FlexInput>
          </Row>

          <Row>
            <FlexInput>
              <FloatingSelect
                label="Área de Atuação"
                value={personFormData.expertise || ""}
                onChange={(e) => handlePersonChange("expertise", e.target.value)}
                error={errors.expertise}
              >
                <option value="" disabled hidden></option>
                <option value="Fisioterapeuta">Fisioterapeuta</option>
                <option value="Médico Ortopedista">Médico Ortopedista</option>
                <option value="Médico Reumatologista">Médico Reumatologista</option>
                <option value="Técnico em Imobilização">Técnico em Imobilização</option>
                <option value="Terapeuta Ocupacional">Terapeuta Ocupacional</option>
              </FloatingSelect>
            </FlexInput>
            <FlexInput>
              <OutlinedInput
                inputName="professional_number"
                label={
                  personFormData.expertise === "Técnico em Imobilização"
                    ? "Nº do Registro (Opcional)"
                    : "Nº do Conselho/Registro Profissional (ex: CREFITO 12345)"
                }
                handleChange={(val) => handlePersonChange("professional_number", val)}
                value={personFormData.professional_number || ""}
                error={errors.professional_number}
              />
            </FlexInput>
          </Row>

          <Row>
            <FlexInput flex={2}>
              <OutlinedInput
                inputName="email"
                label="E-mail principal (acesso à plataforma)"
                handleChange={(val) => handleLicenseeChange("email", val)}
                value={licenseeFormData.email || ""}
                error={errors.email}
              />
            </FlexInput>
            <FlexInput flex={1}>
              <OutlinedInput
                inputName="cellphone"
                label="Celular (WhatsApp)"
                handleChange={(val) => handlePersonChange("cellphone", phoneMask(val))}
                value={personFormData.cellphone || ""}
                error={errors.cellphone}
              />
            </FlexInput>
          </Row>
        </InputGroup>
      </SectionBlock>

      {/* BLOCO 2: DADOS DA EMPRESA (APENAS SE PJ) */}
      {profileType === "PJ" && (
        <SectionBlock>
          <SectionHeader>
            <SectionTitleWrapper>
              <SectionTitleRow>
                <SectionTitle>
                  <FiBriefcase /> Dados Cadastrais da Empresa (PJ)
                </SectionTitle>
              </SectionTitleRow>
              <SectionSubtitle>
                Preencha a razão social e CNPJ da clínica, consultório ou empresa contratante.
              </SectionSubtitle>
            </SectionTitleWrapper>
          </SectionHeader>

          <InputGroup>
            <Row>
              <FlexInput flex={2}>
                <OutlinedInput
                  inputName="companyName"
                  label="Razão Social"
                  handleChange={(val) => handleLicenseeChange("companyName", val)}
                  value={licenseeFormData.companyName || ""}
                  error={errors.companyName}
                />
              </FlexInput>
              <FlexInput flex={1}>
                <OutlinedInput
                  inputName="cnpj"
                  label="CNPJ"
                  handleChange={(val) => handleLicenseeChange("cnpj", cnpjMask(val))}
                  value={licenseeFormData.cnpj || ""}
                  error={errors.cnpj}
                />
              </FlexInput>
            </Row>

            <Row>
              <FlexInput flex={2}>
                <OutlinedInput
                  inputName="fantasyName"
                  label="Nome Fantasia"
                  handleChange={(val) => handleLicenseeChange("fantasyName", val)}
                  value={licenseeFormData.fantasyName || ""}
                  error={errors.fantasyName}
                />
              </FlexInput>
              <FlexInput flex={1}>
                <OutlinedInput
                  inputName="phone"
                  label="Telefone Comercial"
                  handleChange={(val) => handleLicenseeChange("phone", phoneMask(val))}
                  value={licenseeFormData.phone || ""}
                  error={errors.phone}
                />
              </FlexInput>
            </Row>
          </InputGroup>
        </SectionBlock>
      )}

      {/* FOOTER DE AÇÕES */}
      <Footer>
        <SecondaryButton
          type="button"
          text="Voltar"
          onClick={prevStep}
          style={{ minWidth: "120px" }}
        />

        <PrimaryButton
          type="button"
          text="Avançar"
          onClick={validateAndProceed}
          style={{ minWidth: "140px" }}
        />
      </Footer>
    </div>
  );
}
